import React, { useState, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isValidPhone = (v) => v === '' || /^[+]?[\d\s\-()]{7,15}$/.test(v.trim());

const validate = (form) => {
  const e = {};
  if (!form.name.trim()) e.name = 'nameRequired';
  if (!form.email.trim()) e.email = 'emailRequired';
  else if (!isValidEmail(form.email)) e.email = 'emailInvalid';
  if (form.phone && !isValidPhone(form.phone)) e.phone = 'phoneInvalid';
  if (!form.subject.trim()) e.subject = 'subjectRequired';
  if (!form.message.trim()) e.message = 'messageRequired';
  else if (form.message.trim().length < 20) e.message = 'messageTooShort';
  return e;
};

const WHATSAPP_NUMBER = '917779444637';

const FieldError = ({ msg, t }) => (
  <div className="field-error">
    <i className="bi bi-exclamation-circle-fill"></i>
    {t('contact.err_' + msg)}
  </div>
);

const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sendMethod, setSendMethod] = useState('email');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      const errs = validate({ ...form, [name]: value });
      setErrors(prev => ({ ...prev, [name]: errs[name] }));
    }
  }, [form, touched]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const errs = validate(form);
    setErrors(prev => ({ ...prev, [name]: errs[name] }));
  }, [form]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true, subject: true, message: true });
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    if (sendMethod === 'whatsapp') {
      const text = encodeURIComponent(
        '*' + t('contact.formTitle') + '*\n\n' +
        '*' + t('contact.nameLabel') + ':* ' + form.name + '\n' +
        '*' + t('contact.emailLabel') + ':* ' + form.email + '\n' +
        (form.phone ? '*' + t('contact.phoneLabel') + ':* ' + form.phone + '\n' : '') +
        '*' + t('contact.subjectLabel') + ':* ' + form.subject + '\n\n' +
        '*' + t('contact.messageLabel') + ':*\n' + form.message
      );
      window.open('https://wa.me/' + WHATSAPP_NUMBER + '?text=' + text, '_blank');
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setTouched({});
      setTimeout(() => setSubmitted(false), 6000);
      return;
    }

    // ── Email via mailto ──
    const mailBody = encodeURIComponent(
      'Name: ' + form.name + '\n' +
      'Email: ' + form.email + '\n' +
      (form.phone ? 'Phone: ' + form.phone + '\n' : '') +
      '\nMessage:\n' + form.message
    );
    window.open(
      'mailto:chaitraleefoundation@gmail.com' +
      '?subject=' + encodeURIComponent(form.subject) +
      '&body=' + mailBody,
      '_blank'
    );
    setSubmitted(true);
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setTouched({});
    setTimeout(() => setSubmitted(false), 7000);

  }, [form, sendMethod, t]);

  const controlClass = (name) => {
    if (!touched[name]) return 'cf-form-control';
    return 'cf-form-control ' + (errors[name] ? 'is-invalid' : 'is-valid');
  };

  const socials = [
    { icon: 'bi-instagram', href: 'https://instagram.com/chaitraleefoundation' },
    { icon: 'bi-facebook', href: 'https://facebook.com/chaitraleefoundation' },
    { icon: 'bi-youtube', href: '#!' },
    { icon: 'bi-globe2', href: 'https://www.chaitraleefoundationpune.com' },
  ];

  return (
    <section className="section-pad">
      <div className="container">
        <div className="row g-5 align-items-start">

          <div className="col-lg-4">
            <h2 className="section-title">{t('contact.title')}</h2>
            <div className="divider-brand"></div>
            <p className="body-text">{t('contact.subtitle')}</p>

            <div className="d-flex flex-column gap-4 mb-4">
              {[
                { icon: 'bi-geo-alt-fill', text: t('contact.address'), cls: 'contact-icon-primary' },
                { icon: 'bi-whatsapp', text: '+91 7779444637', cls: 'contact-icon-secondary' },
                { icon: 'bi-telephone-fill', text: '+91 7779444637 / 9011017735', cls: 'contact-icon-primary' },
                { icon: 'bi-envelope-fill', text: 'chaitraleefoundation@gmail.com', cls: 'contact-icon-primary' },
                { icon: 'bi-globe', text: t('contact.website'), cls: 'contact-icon-primary' },
              ].map(({ icon, text, cls }) => (
                <div className="contact-info-item" key={icon}>
                  <div className={'contact-info-icon ' + cls}><i className={'bi ' + icon}></i></div>
                  <div className="contact-info-text">{text}</div>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <div className="font-sans text-mute mb-2" style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {t('contact.followUs')}
              </div>
              <div className="d-flex gap-2">
                {socials.map(({ icon, href }) => (
                  <a key={icon} href={href} target="_blank" rel="noopener noreferrer" className="social-icon-btn">
                    <i className={'bi ' + icon}></i>
                  </a>
                ))}
              </div>
            </div>

            <div className="mt-4 p-3 rounded-3 bg-card border border-site">
              <div className="font-sans text-mute mb-2" style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Quick WhatsApp
              </div>
              <a
                href={'https://wa.me/' + WHATSAPP_NUMBER + '?text=' + encodeURIComponent('Hi! I would like to know more about Chaitralee Foundation.')}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent w-100 justify-content-center"
                style={{ textDecoration: 'none' }}
              >
                <i className="bi bi-whatsapp"></i> Chat on WhatsApp
              </a>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="form-card">
              <div className="form-title">{t('contact.formTitle')}</div>

              {submitted && (
                <div className="success-alert">
                  <i className="bi bi-check-circle-fill" style={{ fontSize: '1.3rem' }}></i>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t('contact.successTitle')}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>{t('contact.successMsg')}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="row g-3">

                  <div className="col-md-6">
                    <label className="cf-form-label">{t('contact.nameLabel')} <span className="text-brand">*</span></label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} onBlur={handleBlur}
                      className={controlClass('name')} placeholder={t('contact.namePlaceholder')} autoComplete="name" />
                    {touched.name && errors.name && <FieldError msg={errors.name} t={t} />}
                  </div>

                  <div className="col-md-6">
                    <label className="cf-form-label">{t('contact.emailLabel')} <span className="text-brand">*</span></label>
                    <input type="email" name="email" value={form.email} onChange={handleChange} onBlur={handleBlur}
                      className={controlClass('email')} placeholder={t('contact.emailPlaceholder')} autoComplete="email" />
                    {touched.email && errors.email && <FieldError msg={errors.email} t={t} />}
                  </div>

                  <div className="col-md-6">
                    <label className="cf-form-label">{t('contact.phoneLabel')}</label>
                    <input type="tel" name="phone" value={form.phone} onChange={handleChange} onBlur={handleBlur}
                      className={controlClass('phone')} placeholder={t('contact.phonePlaceholder')} autoComplete="tel" />
                    {touched.phone && errors.phone && <FieldError msg={errors.phone} t={t} />}
                  </div>

                  <div className="col-md-6">
                    <label className="cf-form-label">{t('contact.subjectLabel')} <span className="text-brand">*</span></label>
                    <input type="text" name="subject" value={form.subject} onChange={handleChange} onBlur={handleBlur}
                      className={controlClass('subject')} placeholder={t('contact.subjectPlaceholder')} />
                    {touched.subject && errors.subject && <FieldError msg={errors.subject} t={t} />}
                  </div>

                  <div className="col-12">
                    <label className="cf-form-label">{t('contact.messageLabel')} <span className="text-brand">*</span></label>
                    <textarea name="message" value={form.message} onChange={handleChange} onBlur={handleBlur}
                      className={'cf-form-control cf-form-textarea' + (touched.message ? (errors.message ? ' is-invalid' : ' is-valid') : '')}
                      placeholder={t('contact.messagePlaceholder')} rows={5} />
                    {touched.message && errors.message && <FieldError msg={errors.message} t={t} />}
                    <div className="font-sans text-mute mt-1" style={{ fontSize: '0.72rem' }}>
                      {form.message.trim().length}/500 {t('contact.characters')}
                    </div>
                  </div>

                  <div className="col-12">
                    <span className="send-method-label">{t('contact.sendVia')}</span>
                    <div className="send-method-group">
                      <button type="button"
                        className={'send-method-btn' + (sendMethod === 'email' ? ' selected-email' : '')}
                        onClick={() => setSendMethod('email')}>
                        <i className="bi bi-envelope-fill"></i>
                        {t('contact.sendEmail')}
                        {sendMethod === 'email' && <i className="bi bi-check-circle-fill ms-1" style={{ fontSize: '0.85rem' }}></i>}
                      </button>
                      <button type="button"
                        className={'send-method-btn' + (sendMethod === 'whatsapp' ? ' selected-whatsapp' : '')}
                        onClick={() => setSendMethod('whatsapp')}>
                        <i className="bi bi-whatsapp"></i>
                        {t('contact.sendWhatsApp')}
                        {sendMethod === 'whatsapp' && <i className="bi bi-check-circle-fill ms-1" style={{ fontSize: '0.85rem' }}></i>}
                      </button>
                    </div>
                  </div>

                  <div className="col-12">
                    <button type="submit" disabled={sending}
                      className={'btn-brand w-100 justify-content-center py-3' + (sending ? ' opacity-75' : '')}
                      style={{ fontSize: '1rem' }}>
                      {sending
                        ? <><i className="bi bi-hourglass-split"></i> {t('contact.sending')}</>
                        : sendMethod === 'whatsapp'
                          ? <><i className="bi bi-whatsapp"></i> {t('contact.submitWhatsApp')}</>
                          : <><i className="bi bi-send-fill"></i> {t('contact.submitBtn')}</>}
                    </button>
                    <p className="font-sans text-mute text-center mt-2 mb-0" style={{ fontSize: '0.73rem' }}>
                      <i className="bi bi-lock-fill me-1"></i>{t('contact.privacyNote')}
                    </p>
                  </div>

                </div>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactForm;
